import { useMemo } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { useGetHarnessInvoicesQuery } from "@/api/providers/invoices"
import { useGetHarnessCustomerPaymentMethodsQuery } from "@/api/providers/payment-methods"
import { useGetHarnessSubscriptionsQuery } from "@/api/providers/subscriptions"
import { CheckIcon } from "@/common/elements/icons"
import { Button } from "@/common/elements/button"
import { Link } from "@/common/elements/link"
import { Skeleton } from "@/common/elements/skeleton"
import { useSelectedCustomerId } from "@/common/hooks/use-selected-customer-id"
import {
  formatCurrency,
  formatDate,
  getTotalAmountCents,
} from "@/plugins/stub/billing-data/utils"
import { ApiSession } from "@/common/auth/utils"
import { AuthenticatedUser } from "@/common/keycloak/keycloak"
import {
  formatPaymentMethodSummary,
  UiInvoiceStatus,
  mapHarnessInvoiceToUi,
  mapHarnessPaymentMethodToUi,
  mapHarnessSubscriptionToUi,
} from "@/plugins/controls/common/billing-mappers"

function formatDateOrFallback(dateText: string | null) {
  return dateText ? formatDate(dateText) : "N/A"
}

export function DashboardControl(_props: {
  authUser: AuthenticatedUser
  apiSession: ApiSession | null
  onLogout: () => Promise<void>
}) {
  const selectedCustomerId = useSelectedCustomerId()
  const subscriptionsQueryArg = selectedCustomerId !== null ? { customerId: selectedCustomerId } : skipToken
  const invoicesQueryArg = selectedCustomerId !== null ? { customerId: selectedCustomerId } : skipToken
  const paymentMethodsQueryArg = selectedCustomerId !== null ? { customerId: selectedCustomerId } : skipToken

  const {
    data: subscriptionData = [],
    isLoading: isSubscriptionsLoading,
    isFetching: isSubscriptionsFetching,
  } = useGetHarnessSubscriptionsQuery(subscriptionsQueryArg)
  const {
    data: invoiceData = [],
    isLoading: isInvoicesLoading,
    isFetching: isInvoicesFetching,
  } = useGetHarnessInvoicesQuery(invoicesQueryArg)
  const {
    data: paymentMethodData = [],
    isLoading: isPaymentMethodsLoading,
    isFetching: isPaymentMethodsFetching,
    isError: isPaymentMethodsError,
  } = useGetHarnessCustomerPaymentMethodsQuery(paymentMethodsQueryArg)

  const isOverviewLoading = selectedCustomerId !== null
    && (isSubscriptionsLoading
      || isSubscriptionsFetching
      || isInvoicesLoading
      || isInvoicesFetching
      || isPaymentMethodsLoading
      || isPaymentMethodsFetching)

  const subscriptions = useMemo(
    () => subscriptionData.map((subscription, index) => mapHarnessSubscriptionToUi(subscription, index)),
    [subscriptionData]
  )
  const invoices = useMemo(() => invoiceData.map((invoice, index) => mapHarnessInvoiceToUi(invoice, index)), [invoiceData])
  const paymentMethods = useMemo(
    () => paymentMethodData.map((paymentMethod) => mapHarnessPaymentMethodToUi(paymentMethod)),
    [paymentMethodData]
  )

  const outstandingInvoices = invoices.filter(
    (invoice) => invoice.status === UiInvoiceStatus.Due || invoice.status === UiInvoiceStatus.Overdue
  )
  const overdueInvoices = outstandingInvoices.filter((invoice) => invoice.status === UiInvoiceStatus.Overdue)

  let invoiceAlert = null;

  if (overdueInvoices.length > 0) 
  {
    invoiceAlert = {
      message: `${overdueInvoices.length} invoice${overdueInvoices.length === 1 ? " is" : "s are"} overdue`,
      containerClasses: "border-rose-300 bg-rose-50 text-rose-900",
      iconContainerClasses: "bg-rose-600 text-white",
      icon: "!" as "?" | "!" | "check",
    }
  } else if (outstandingInvoices.length > 0) {
    invoiceAlert = {
      message: `${outstandingInvoices.length} invoice${outstandingInvoices.length === 1 ? " is" : "s are"} due`,
      containerClasses: "border-amber-300 bg-amber-50 text-amber-900",
      iconContainerClasses: "bg-amber-600 text-white",
      icon: "!" as "?" | "!" | "check",
    }
  }

  const defaultPaymentMethod = paymentMethods.find((method) => method.isDefault)

  let paymentStatusAlert = null;

  if (isPaymentMethodsError)
  {
    paymentStatusAlert = {
      message: "Error validating payment method",
      containerClasses: "border-rose-300 bg-rose-50 text-rose-900",
      iconContainerClasses: "bg-rose-600 text-white",
      icon: "!" as "?" | "!" | "check",
    }
  } else if (paymentMethods.length == 0)
  {
    paymentStatusAlert = {
      message: "Missing payment method",
      containerClasses: "border-rose-300 bg-rose-50 text-rose-900",
      iconContainerClasses: "bg-rose-600 text-white",
      icon: "!" as "?" | "!" | "check",
    }
  } else if (!defaultPaymentMethod)
  {
    paymentStatusAlert = {
      message: "Missing default payment method",
      containerClasses: "border-rose-300 bg-rose-50 text-rose-900",
      iconContainerClasses: "bg-rose-600 text-white",
      icon: "!" as "?" | "!" | "check",
    }
  } else if (defaultPaymentMethod.isDisabled)
  {
    paymentStatusAlert = {
      message: "Default payment method is disabled",
      containerClasses: "border-rose-300 bg-rose-50 text-rose-900",
      iconContainerClasses: "bg-rose-600 text-white",
      icon: "!" as "?" | "!" | "check",
    }
  }

  const monthlyRecurringAmount = getTotalAmountCents(
    subscriptions.map((subscription) => ({ amountCents: subscription.monthlyAmountCents }))
  )
  const defaultPaymentMethodText = formatPaymentMethodSummary(defaultPaymentMethod)
  const nextDueInvoice = [...outstandingInvoices].sort(
    (left, right) => {
      const leftDueDate = left.dueDate ? new Date(left.dueDate).getTime() : Number.MAX_SAFE_INTEGER
      const rightDueDate = right.dueDate ? new Date(right.dueDate).getTime() : Number.MAX_SAFE_INTEGER
      return leftDueDate - rightDueDate
    }
  )[0]

  return (
    <div className="space-y-6">
      <article className="rounded-lg border bg-muted/20 p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <img src="/assets/cloudfit-logo.png" alt="CloudFit Software" className="h-10 w-auto" loading="lazy" />
            <div>
              <h2 className="text-base font-semibold">Welcome to CloudFit</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                CloudFit helps organizations transform and secure mission-critical Microsoft Cloud workloads with compliance-focused managed services, including CMMC, licensing, and 24/7 proactive management.
              </p>
            </div>
          </div>
          <Button asChild size="sm" variant="outline">
            <a href="https://cloudfitsoftware.com" target="_blank" rel="noopener noreferrer">
              Learn more
            </a>
          </Button>
        </div>
      </article>

      <section className="sm:rounded-lg md:border sm:bg-card sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Billing Overview</h2>
        </div>
      </div>

      <div className="mt-6 grid gap-3">
        <div className="rounded-md border bg-muted/30 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Subscriptions</p>
              {isOverviewLoading ? (
                <div className="mt-2 space-y-2">
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-3 w-64" />
                </div>
              ) : (
                <>
                  <p className="mt-2 text-2xl font-semibold">{subscriptions.length}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatCurrency(monthlyRecurringAmount)} / next billing amount across all active subscriptions
                  </p>
                </>
              )}
            </div>
            <Button asChild size="sm" variant="outline">
              <Link to="/subscriptions">View my subscriptions</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <article className="rounded-lg border p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Invoice Status</h3>
            <Button asChild size="sm" variant="outline">
              <Link to="/invoices">View Invoices</Link>
            </Button>
          </div>

          <div className="mt-3 space-y-3 text-sm">
            {isOverviewLoading ? (
              <>
                <div className="rounded-md border p-3">
                  <Skeleton className="h-5 w-52" />
                </div>
                <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </>
            ) : null}

            {!isOverviewLoading && invoiceAlert && (
              <div className={`rounded-md border p-3 ${invoiceAlert.containerClasses}`}>
                <p className="inline-flex items-center gap-2 font-medium">
                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${invoiceAlert.iconContainerClasses}`}>
                    {invoiceAlert.icon === "check" ? (
                      <CheckIcon className="h-3.5 w-3.5" />
                    ) : (
                      <span className="text-xs leading-none font-bold">{invoiceAlert.icon}</span>
                    )}
                  </span>
                  {invoiceAlert.message}
                </p>
              </div>
            )}

            {!isOverviewLoading && nextDueInvoice ? (
              <div className="rounded-md border bg-muted/20 p-3">
                <p className="font-medium">{overdueInvoices.length > 0 ? 'Overdue' : 'Open'} invoice</p>
                <p className="mt-1 text-muted-foreground">
                    {nextDueInvoice.id} {overdueInvoices.length > 0 ? 'was' : 'is'} due {formatDateOrFallback(nextDueInvoice.dueDate)} for {formatCurrency(nextDueInvoice.amountCents)}.
                </p>
              </div>
            ) : !isOverviewLoading ? (
              <div className="rounded-md border bg-muted/20 p-3">
                <p className="font-medium">No open invoices</p>
                <p className="mt-1 text-muted-foreground">All invoices have been paid.</p>
              </div>
            ) : null}
          </div>
        </article>

        <article className="rounded-lg border p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Payment Status</h3>
            <Button asChild size="sm" variant="outline">
              <Link to="/payment-methods">Update Payment Method</Link>
            </Button>
          </div>
          <div className="mt-3 space-y-3 text-sm">
            {isOverviewLoading ? (
              <>
                <div className="rounded-md border p-3">
                  <Skeleton className="h-5 w-56" />
                </div>
                <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-64" />
                </div>
              </>
            ) : null}

            {!isOverviewLoading && paymentStatusAlert && (
              <div className={`rounded-md border p-3 ${paymentStatusAlert.containerClasses}`}>
                <p className="inline-flex items-center gap-2 font-medium">
                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${paymentStatusAlert.iconContainerClasses}`}>
                    {paymentStatusAlert.icon === "check" ? (
                      <CheckIcon className="h-3.5 w-3.5" />
                    ) : (
                      <span className="text-xs leading-none font-bold">{paymentStatusAlert.icon}</span>
                    )}
                  </span>
                  {paymentStatusAlert.message}
                </p>
              </div>
            )}
            
            {!isOverviewLoading ? (
              <div className="rounded-md border bg-muted/20 p-3">
              <p className="font-medium">Default payment method</p>
              <p className="mt-1 text-muted-foreground">{defaultPaymentMethodText}</p>
              </div>
            ) : null}
          </div>
        </article>
      </div>
      </section>
    </div>
  )
}
